import axios from 'axios';

import type {
  MoiVendorAssignmentRuleCreateParams,
  MoiVendorAssignmentRuleInfo,
  MoiVendorAssignmentRuleUpdateParams,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

/**
 * Mutating calls for the MOI assignment rules screen. Each hands the error to `reportAxiosError`
 * (which reports it and opens the error dialog) and rethrows, so the caller knows the write did not
 * land and can keep its dialog open or roll back an optimistic change.
 *
 * The `/sysconfig/api/v1` prefix — not the `/api/v1` the Guardrails sibling uses — is what the
 * service registry mounts a module's `get_sysconfig_blueprint` under. Verified against the
 * generated `openapi.json`, which is emitted from the registered app.
 */
export const moiAssignmentRulesUrl = (organizationId: number): string =>
  `/sysconfig/api/v1/organizations/${organizationId}/moi_vendor_assignment/rules`;

export const createMoiAssignmentRule = async (
  organizationId: number,
  values: MoiVendorAssignmentRuleCreateParams
): Promise<MoiVendorAssignmentRuleInfo> => {
  try {
    const response = await axios.post<MoiVendorAssignmentRuleInfo>(moiAssignmentRulesUrl(organizationId), values);
    return response.data;
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};

export const updateMoiAssignmentRule = async (
  organizationId: number,
  ruleId: number,
  values: MoiVendorAssignmentRuleUpdateParams
): Promise<MoiVendorAssignmentRuleInfo> => {
  try {
    const response = await axios.put<MoiVendorAssignmentRuleInfo>(
      `${moiAssignmentRulesUrl(organizationId)}/${ruleId}`,
      values
    );
    return response.data;
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};

export const deleteMoiAssignmentRule = async (organizationId: number, ruleId: number): Promise<void> => {
  try {
    await axios.delete(`${moiAssignmentRulesUrl(organizationId)}/${ruleId}`);
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};

/** Two bodiless POST routes rather than one PATCH with a flag — that is the shape the BE exposes
 *  (`…/{rule_id}/enable` and `…/{rule_id}/disable`), and it differs from the guardrails sibling's
 *  `PATCH …/enable {is_enabled}`. */
export const setMoiAssignmentRuleEnabled = async (
  organizationId: number,
  ruleId: number,
  isEnabled: boolean
): Promise<void> => {
  try {
    await axios.post(`${moiAssignmentRulesUrl(organizationId)}/${ruleId}/${isEnabled ? 'enable' : 'disable'}`);
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};

/** The body is the rule set's whole target order, not a delta: the BE requires an exact permutation
 *  of every live MOI assignment rule in the organization, disabled ones included, and answers 400
 *  otherwise without reordering anything. */
export const reorderMoiAssignmentRules = async (organizationId: number, orderedIds: number[]): Promise<void> => {
  try {
    await axios.post(`${moiAssignmentRulesUrl(organizationId)}/reorder`, { ordered_ids: orderedIds });
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};
